import './index.css'
import * as React from 'react'
import { MdBusinessCenter } from '@react-icons/all-files/md/MdBusinessCenter'
import InlineSVG from 'svg-inline-react'
import type { Recipient } from 'types/list'
import { RECIPIENT_GROUP, USER } from '@disco/constants/RecipientTypes'
import RecipientLabel from 'components/common/email-groups/recipient-label'
import Tooltip from 'components/ui/tooltip'

/**
 * Recipient tag component with tooltip functionality and removal option
 * Displays recipient information as a clickable tag with business/group details in tooltips
 * Supports different recipient types with appropriate tooltips and in-place editing
 * @param appearance - Display mode: 'oneline' or 'twoline'
 * @param recipient - Recipient object with type, name, email, and business information
 * @param onClick - Callback when tag is clicked
 * @param onRemove - Callback when remove button is clicked
 * @param onInplaceEdit - Callback for in-place editing (for recipient groups)
 * @param index - Unique index for the tag (workaround for non-unique recipient IDs)
 */
class RecipientTag extends React.Component<{
  appearance?: 'oneline' | 'twoline'
  recipient: Recipient
  onClick?: () => unknown
  onRemove?: (...args: Array<any>) => any
  onInplaceEdit?: () => unknown
  index: number // need for workaround because recipient can have non-unique id
}> {
  static defaultProps: {
    appearance: 'oneline' | 'twoline'
  } = {
    appearance: 'oneline',
  }

  _renderUserTooltip(): null | React.ReactElement<'ul'> {
    const { business_ids, businesses, show_businesses } = this.props.recipient
    if (
      !business_ids ||
      business_ids.length === 0 ||
      !businesses ||
      businesses.length === 0 ||
      show_businesses === false
    )
      return null
    const businessList = businesses
      .filter(business => {
        return business_ids.indexOf(business.id) > -1
      })
      .map((business, index) => {
        return (
          <li key={index}>
            <MdBusinessCenter />
            {business.name}
          </li>
        )
      })
    return (
      <ul className="RecipientsTooltip_Content">
        <li className="">Businesses:</li>
        {businessList}
      </ul>
    )
  }

  _renderGroupTooltip(): React.ReactElement<'ul'> {
    const { recipients } = this.props.recipient

    if (!recipients || recipients.length === 0) {
      return <ul className="RecipientsTooltip_Content">group is empty</ul>
    }

    const recipientList = recipients.slice(0, 3).map((recipient, index) => {
      return (
        <li key={index} className="Form_Tag">
          <RecipientLabel recipient={recipient} />
        </li>
      )
    })
    let hasMore: React.ReactNode = null

    if (recipients.length > 3) {
      const moreRecipients = recipients.length - 3
      let hasMoreText = `+ ${moreRecipients} recipient`
      if (moreRecipients > 1) hasMoreText += 's'
      hasMore = <span>{hasMoreText}</span>
    }

    let displayInplaceEdit: React.ReactNode = null

    if (this.props.onInplaceEdit) {
      const { onInplaceEdit } = this.props
      displayInplaceEdit = (
        <li className="Form_Link">
          <a
            href="#"
            onClick={() => {
              onInplaceEdit()
            }}
          >
            Edit...
          </a>
        </li>
      )
    }

    return (
      <ul className="RecipientsTooltip_Content">
        {recipientList}
        <li className="Form_Tag is-italic">{hasMore}</li>
        {displayInplaceEdit}
      </ul>
    )
  }

  _renderTooltipContents(): null | React.ReactElement<'ul'> {
    const { type, business_ids } = this.props.recipient
    if (type !== USER && type !== RECIPIENT_GROUP) return null
    if (type === USER && (!business_ids || business_ids.length === 0))
      return null

    switch (type) {
      case USER:
        return this._renderUserTooltip()

      case RECIPIENT_GROUP:
        return this._renderGroupTooltip()

      default:
        return null
    }
  }

  render(): React.ReactElement<'button'> {
    const tooltipContent = this._renderTooltipContents()

    // TODO: merge with Form_Tag
    return (
      <button
        id={`RecipientTag-${this.props.index}`}
        className="Form_Tag"
        onClick={this.props.onClick}
      >
        <Tooltip content={tooltipContent} position="bottom">
          <div className="tag__name">
            <RecipientLabel
              appearance={this.props.appearance}
              recipient={this.props.recipient}
            />
          </div>
        </Tooltip>
        <div className="tag__remove" onClick={this.props.onRemove}>
          <InlineSVG
            src={require('!svg-inline!assets/icons/tag-remove.svg')}
            width="16"
            height="16"
            raw
          />
        </div>
      </button>
    )
  }
}

export default RecipientTag
